import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label rendered after the box (leading edge flips under RTL). */
  label?: ReactNode;
  /** Optional helper / hint text below the label. */
  description?: ReactNode;
}

/**
 * Styled native `<input type="checkbox">`. We deliberately rely on the
 * browser's built-in checkbox affordances (focus, indeterminate state, screen
 * reader name) rather than rolling our own — accessibility is free that way,
 * and `accent-color` lets the tick follow the tenant brand without a custom
 * SVG. 18px is the sister app's box size.
 */
/**
 * The BOX treatment on its own, exported for the handful of call sites that
 * legitimately own their `<label>` — a column-picker row, a bordered option
 * card. Those cannot use `<Checkbox label=…>` (its own label would nest inside
 * theirs, which is invalid HTML), and before this they each re-invented the
 * box: `h-4 w-4` here, `h-5 w-5 rounded` there, one with `accent-primary` and
 * one without.
 *
 * Reach for this ONLY when the surrounding label already exists and carries its
 * own hit area. For everything else use `<Checkbox>`, which brings the label,
 * the description and the touch floor with it.
 *
 * THE RADIUS IS THE ONE ARBITRARY VALUE IN THIS FILE, and deliberately so. The
 * shape ramp has no 4px step — `--radius-xs` is 8px and `--radius-2xs` is 2px —
 * and `--radius-xs`'s comment in design-tokens.css still lists "checkbox" among
 * its tenants — one tenant out of date as of this line. 8px on an 18px box is
 * nearly a squircle, and the mock's 4px-on-16px is not that at all; rounding is
 * most of a checkbox's silhouette when the box is this small.
 * Adding a seventh role to a ramp the token file argues hard for keeping at six
 * would cost more than it buys, so the value is spelled here, once, beside the
 * reason.
 *
 * The BOX stays 18px against the mock's 16px. That difference is a click target
 * on a camp tablet, not a corner, and it is the direction we would err in
 * anyway.
 */
export const checkboxBoxClass = cn(
  'h-[1.125rem] w-[1.125rem] shrink-0 rounded-[4px] border-input accent-primary',
  focusRing,
  cn('disabled:cursor-not-allowed', disabledFade)
);

const boxClass = checkboxBoxClass;

/* The 44px floor for a checkbox with NO visible label — the table select-all
 * and the per-row boxes. The floor used to live only on the `<label>` below,
 * which is right when there IS a label (the whole label is the hit area) but
 * left the bare box at 18px exactly where it matters most: a roster table on a
 * camp tablet, where ticking the wrong row is a payroll edit.
 *
 * The target grows on a WRAPPER LABEL, not on the input. Sizing the input
 * itself does not work: `<input type="checkbox">` is painted by the browser
 * (`appearance: auto`), so `h-11 w-11` makes Chrome draw a 44px checkbox — a
 * giant box in a dense table — rather than a small box with a large target.
 * A wrapping `<label>` forwards its clicks to the input it labels, so the
 * target is 44px while the control stays 18px.
 *
 * The negative margin hands the extra space back to the layout, so a table row
 * keeps its height while the finger still gets its area. */
const bareTouchWrapper = cn(
  'inline-flex items-center justify-center',
  '[@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 [@media(pointer:coarse)]:-m-[0.8125rem]'
);

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id: idProp, disabled, ...props }, ref) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    if (label === undefined && description === undefined) {
      return (
        <label htmlFor={id} className={bareTouchWrapper}>
          <input
            ref={ref}
            id={id}
            type="checkbox"
            disabled={disabled}
            className={cn(boxClass, className)}
            {...props}
          />
        </label>
      );
    }
    return (
      <label
        htmlFor={id}
        className={cn(
          // The whole label is the hit area, so the 44px floor belongs here
          // rather than on the 18px box.
          'flex items-start gap-element text-copy text-foreground',
          '[@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:items-center',
          // `has-[:disabled]` rather than the React prop, so the fade also
          // catches a checkbox disabled by an ancestor `<fieldset disabled>` —
          // which the prop cannot see and which the box's own `disabled:` used
          // to be the only thing covering.
          'has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50'
        )}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
          // THE FADE BELONGS TO THE WHOLE CONTROL, ONCE. `checkboxBoxClass`
          // carries `disabledFade` for the bare-box export, where the box IS
          // the control. Here the wrapper already fades label and box together,
          // so leaving the box's own 50% in place multiplied the two and
          // rendered an 18px box at 25% opacity beside its label at 50% — the
          // one part of the control you actually have to see. `opacity-100` in
          // the same variant+group makes tailwind-merge DROP the inherited
          // `disabled:opacity-50` rather than emit both and let source order
          // decide.
          className={cn(boxClass, 'mt-0.5 disabled:opacity-100', className)}
          {...props}
        />
        <span className="flex flex-col gap-0.5">
          {label && <span className="leading-tight">{label}</span>}
          {description && (
            <span className="text-copy-sm text-muted-foreground">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  }
);
Checkbox.displayName = 'Checkbox';
